import type { MatchResponse, KeywordMatch, EvidenceItem } from '@applicator/shared'
import { useState, useEffect } from 'react'

import { matchAts } from '../lib/api/atsClient'

const STORAGE_KEYS = {
  jobText: 'ats_playground_job_text',
  resumeText: 'ats_playground_resume_text',
}

const SAMPLE_DATA = {
  jobText: `We are looking for a Senior Software Engineer with extensive experience in Python, React, and AWS. The ideal candidate should have:

- 5+ years of experience in full-stack development
- Strong proficiency in Python and FastAPI
- Experience with React and modern frontend frameworks
- Cloud infrastructure experience with AWS (EC2, S3, Lambda)
- Knowledge of Docker and containerization
- Strong problem-solving skills and attention to detail
- Excellent communication skills and ability to work in a team
- Leadership experience in technical projects
- Strategic thinking and ability to drive technical decisions`,

  resumeText: `John Doe
Senior Software Engineer
Email: john.doe@example.com | Phone: (555) 123-4567

SUMMARY
Experienced full-stack developer with 6 years of expertise in Python, React, and cloud technologies. Proven track record of building scalable applications and leading technical initiatives.

SKILLS
- Programming Languages: Python, JavaScript, TypeScript
- Frameworks: React, FastAPI, Node.js
- Cloud & Infrastructure: AWS (EC2, S3), Docker, Kubernetes
- Databases: PostgreSQL, MongoDB
- Tools: Git, CI/CD, Agile methodologies

EXPERIENCE
Senior Software Engineer | Tech Corp | 2020 - Present
- Developed and maintained Python-based microservices using FastAPI
- Built responsive frontend applications with React
- Deployed applications to AWS cloud infrastructure
- Containerized applications using Docker
- Led a team of 3 developers on multiple projects
- Implemented CI/CD pipelines for automated deployments

Software Engineer | StartupXYZ | 2018 - 2020
- Built RESTful APIs with Python and Flask
- Developed React components for user-facing features
- Worked with AWS services for application deployment

EDUCATION
Bachelor of Science in Computer Science
State University | 2014 - 2018`,
}

function AtsPlayground() {
  const [jobText, setJobText] = useState('')
  const [resumeText, setResumeText] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<MatchResponse | null>(null)
  const [expandedKeyword, setExpandedKeyword] = useState<string | null>(null)

  // Load from localStorage on mount
  useEffect(() => {
    const savedJobText = localStorage.getItem(STORAGE_KEYS.jobText)
    const savedResumeText = localStorage.getItem(STORAGE_KEYS.resumeText)
    if (savedJobText) setJobText(savedJobText)
    if (savedResumeText) setResumeText(savedResumeText)
  }, [])

  // Save to localStorage when text changes
  useEffect(() => {
    if (jobText) {
      localStorage.setItem(STORAGE_KEYS.jobText, jobText)
    }
  }, [jobText])

  useEffect(() => {
    if (resumeText) {
      localStorage.setItem(STORAGE_KEYS.resumeText, resumeText)
    }
  }, [resumeText])

  const handleAnalyze = async () => {
    if (!jobText.trim() || !resumeText.trim()) {
      setError('Please provide both job description and resume text')
      return
    }

    setLoading(true)
    setError(null)
    setResult(null)
    setExpandedKeyword(null)

    try {
      const response = await matchAts({
        job_text: jobText,
        resume_text: resumeText,
      })
      setResult(response)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze match')
    } finally {
      setLoading(false)
    }
  }

  const handleLoadSample = () => {
    setJobText(SAMPLE_DATA.jobText)
    setResumeText(SAMPLE_DATA.resumeText)
    setError(null)
    setResult(null)
    setExpandedKeyword(null)
  }

  const handleClear = () => {
    setJobText('')
    setResumeText('')
    setError(null)
    setResult(null)
    setExpandedKeyword(null)
    localStorage.removeItem(STORAGE_KEYS.jobText)
    localStorage.removeItem(STORAGE_KEYS.resumeText)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'found':
        return 'bg-green-100 text-green-800 border-green-300'
      case 'weak':
        return 'bg-amber-100 text-amber-800 border-amber-300'
      case 'missing':
        return 'bg-red-100 text-red-800 border-red-300'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300'
    }
  }

  const renderKeywordSection = (title: string, keywords: KeywordMatch[]) => {
    if (keywords.length === 0) return null

    return (
      <div className="mb-6">
        <h3 className="text-h3 font-semibold mb-3 text-gray-900">{title}</h3>
        <div className="flex flex-wrap gap-2">
          {keywords.map((keyword) => (
            <div key={keyword.keyword} className="relative">
              <button
                onClick={() =>
                  setExpandedKeyword(
                    expandedKeyword === keyword.keyword ? null : keyword.keyword
                  )
                }
                className={`px-3 py-1.5 rounded-md border text-sm font-medium transition-colors cursor-pointer ${getStatusColor(
                  keyword.status
                )} hover:opacity-80`}
                title={`Status: ${keyword.status}, Confidence: ${(keyword.confidence * 100).toFixed(0)}%`}
              >
                {keyword.keyword}
              </button>
              {expandedKeyword === keyword.keyword && keyword.evidence.length > 0 && (
                <div className="absolute z-10 mt-2 left-0 w-96 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Evidence:</div>
                  {keyword.evidence.map((evidence: EvidenceItem, idx: number) => (
                    <div key={idx} className="mb-3 last:mb-0">
                      <div className="text-xs font-medium text-gray-600 mb-1">
                        {evidence.section}
                      </div>
                      <div className="text-sm text-gray-800 bg-gray-50 p-2 rounded border border-gray-200">
                        {evidence.snippet}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Score: {(evidence.score * 100).toFixed(0)}%
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setExpandedKeyword(null)
                    }}
                    className="mt-2 text-xs text-gray-600 hover:text-gray-800"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-h1 mb-6 text-gray-900">ATS Playground</h1>

        {/* Input Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="job-description" className="block text-sm font-medium text-gray-700 mb-2">
              Job Description
            </label>
            <textarea
              id="job-description"
              value={jobText}
              onChange={(e) => setJobText(e.target.value)}
              placeholder="Paste job description here..."
              className="w-full h-64 p-3 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-sm"
            />
          </div>
          <div>
            <label htmlFor="resume-text" className="block text-sm font-medium text-gray-700 mb-2">
              Resume Text
            </label>
            <textarea
              id="resume-text"
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              placeholder="Paste resume text here..."
              className="w-full h-64 p-3 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-sm"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={handleAnalyze}
            disabled={loading || !jobText.trim() || !resumeText.trim()}
            className="px-4 py-2 bg-green-600 text-white rounded-md font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Analyzing...
              </>
            ) : (
              'Analyze'
            )}
          </button>
          <button
            onClick={handleLoadSample}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md font-medium hover:bg-gray-300 transition-colors"
          >
            Load Sample
          </button>
          <button
            onClick={handleClear}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md font-medium hover:bg-gray-300 transition-colors"
          >
            Clear
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="text-sm font-medium text-red-800">Error</div>
            <div className="text-sm text-red-700 mt-1">{error}</div>
          </div>
        )}

        {/* Results Section */}
        {result && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            {/* Match Score */}
            <div className="mb-6">
              <div className="flex items-center gap-4">
                <div className="text-sm font-medium text-gray-600">Match Score</div>
                <div
                  className={`text-4xl font-bold ${
                    result.match_score >= 70
                      ? 'text-green-600'
                      : result.match_score >= 40
                      ? 'text-amber-600'
                      : 'text-red-600'
                  }`}
                >
                  {result.match_score}
                </div>
                <div className="text-sm text-gray-500">/ 100</div>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {result.meta.job_keywords_total} keywords extracted • {result.meta.resume_tokens}{' '}
                resume tokens
              </div>
            </div>

            {/* Keyword Sections */}
            {renderKeywordSection('Hard Skills', result.hard_skills)}
            {renderKeywordSection('Soft Skills', result.soft_skills)}
            {renderKeywordSection('Other', result.other)}

            {result.hard_skills.length === 0 &&
              result.soft_skills.length === 0 &&
              result.other.length === 0 && (
                <div className="text-sm text-gray-500 italic">No keywords found</div>
              )}
          </div>
        )}

        {/* Instructions */}
        {!result && !loading && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="text-sm text-blue-800">
              <strong>Instructions:</strong> Paste a job description and resume text, then click
              "Analyze" to see keyword matches. Click on any keyword chip to view evidence snippets.
              Your inputs are automatically saved to localStorage.
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AtsPlayground
